import React, { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Field, Input } from '../ui';

/**
 * The other names an organization goes by.
 *
 * Directories are built from business cards, letterheads and press, and the
 * same body appears as an acronym, an English rendering or the name it had
 * two governments ago. Each one recorded here is matched exactly like the
 * organization's own name — when a card is scanned, when duplicates are
 * looked for, and when anyone searches.
 */
export const KnownNamesField: React.FC<{
  value: string[];
  onChange: (names: string[]) => void;
  /** The organization's own name, which is never also a known name */
  name?: string;
  className?: string;
}> = ({ value, onChange, name, className = '' }) => {
  const [draft, setDraft] = useState('');

  const add = () => {
    const entry = draft.replace(/\s+/g, ' ').trim();
    if (!entry) return;
    const same = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();
    if (name && same(entry, name)) {
      setDraft('');
      return;
    }
    if (!value.some((held) => same(held, entry))) onChange([...value, entry]);
    setDraft('');
  };

  return (
    <Field
      label="Also known as"
      htmlFor="known-name"
      hint="Acronyms, translations, former names — searched and matched like the name itself"
      className={className}
    >
      {value.length > 0 && (
        <ul className="mb-2 flex flex-wrap gap-1.5">
          {value.map((known) => (
            <li key={known}>
              <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-neutral-100 py-1 pl-3 pr-1 text-[12.5px] text-ink">
                <span className="min-w-0 truncate">{known}</span>
                <button
                  type="button"
                  onClick={() => onChange(value.filter((item) => item !== known))}
                  aria-label={`Remove ${known}`}
                  className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-neutral-200 hover:text-ink"
                >
                  <X className="h-3 w-3" strokeWidth={2} />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="flex gap-2">
        <Input
          id="known-name"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            // Enter adds a name; it must not submit the form
            if (event.key === 'Enter' || event.key === ',') {
              event.preventDefault();
              add();
            }
          }}
          onBlur={add}
          placeholder="SOGUIPAMI"
        />
        <button
          type="button"
          onClick={add}
          disabled={!draft.trim()}
          aria-label="Add this name"
          className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-neutral-200 text-neutral-500 transition-colors hover:bg-neutral-50 hover:text-ink disabled:opacity-40 sm:h-9 sm:w-9"
        >
          <Plus className="h-4 w-4" strokeWidth={2} />
        </button>
      </div>
    </Field>
  );
};
